import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Using FormSubmit.co - free form submission service (no registration needed)
      const formElement = e.target;
      const formDataToSend = new FormData();
      
      formDataToSend.append('name', formData.name);
      formDataToSend.append('Company', formData.company);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('Phone', formData.phone);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('_subject', 'New Inquiry from Big Bag Essentials Website');
      formDataToSend.append('_template', 'table');
      formDataToSend.append('_captcha', 'false');

      const response = await fetch('https://formsubmit.co/ajax/anandsharma@bigbagessentials.in', {
        method: 'POST',
        body: formDataToSend
      });

      const data = await response.json();

      if (data.success === 'true' || response.ok) {
        toast({
          title: "Message Sent Successfully! 🎉",
          description: "Thank you for contacting us. Our team will get back to you within 24 hours.",
        });

        // Reset form
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          message: ''
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast({
        title: "Oops! Something went wrong",
        description: "Please contact us directly at anandsharma@bigbagessentials.in or call +91 70733 39922",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'HEAD OFFICE',
      details: [
        'No. 31, Panchwati Colony,',
        'Gayatri Nagar, Beawar,',
        'Rajasthan - 305901, INDIA'
      ]
    },
    {
      icon: MapPin,
      title: 'FACTORY',
      details: [
        'Village Daulatkheda,',
        'Teh.: Pisangan, Opp. NH. 8,',
        'Near Mangliawas,',
        'Distt. Ajmer - 305203'
      ]
    },
    {
      icon: Phone,
      title: 'PHONE',
      details: [
        { type: 'link', href: 'tel:+917073339922', text: '+91 70733 39922' },
        { type: 'link', href: 'tel:+919414016053', text: '+91 94140 16053' }
      ]
    },
    {
      icon: Mail,
      title: 'EMAIL',
      details: [
        { type: 'link', href: 'mailto:sales@bigbagessentials.in', text: 'sales@bigbagessentials.in' },
        { type: 'link', href: 'mailto:anandsharma@bigbagessential.in', text: 'anandsharma@bigbagessential.in' },
        { type: 'link', href: 'mailto:bigbagessentials@gmail.com', text: 'bigbagessentials@gmail.com' }
      ]
    },
    {
      icon: Clock,
      title: 'WORKING HOURS',
      details: [
        'Monday - Saturday: 9:00 AM - 6:00 PM',
        'Sunday: Closed'
      ]
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-gradient-to-br from-teal-50/50 via-white to-orange-50/30">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div className="max-w-3xl" {...fadeInUp}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold mb-6">
              Get in Touch
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Have questions about our products or need a quote? Our team is here to help.
              Fill out the form below or reach out directly via phone or email.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl font-semibold mb-8">Contact Information</h2>
              <div className="space-y-6">
                {contactInfo.map((info, index) => {
                  const IconComponent = info.icon;
                  return (
                    <Card key={`contact-${info.title}-${index}`}>
                      <CardContent className="pt-6">
                        <div className="flex items-start space-x-4">
                          <div className="bg-primary/10 p-3 rounded-lg">
                            <IconComponent className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-semibold mb-2">{info.title}</h3>
                            <div className="space-y-1 text-sm text-muted-foreground">
                              {info.details.map((detail, detailIdx) => (
                                <div key={`detail-${index}-${detailIdx}`}>
                                  {typeof detail === 'object' && detail.type === 'link' ? (
                                    <a href={detail.href} className="flex items-center space-x-2 text-sm hover:text-primary transition-colors">
                                      {info.icon === Phone && <Phone className="h-4 w-4" />}
                                      {info.icon === Mail && <Mail className="h-4 w-4" />}
                                      <span>{detail.text}</span>
                                    </a>
                                  ) : (
                                    <p>{detail}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div {...fadeInUp} transition={{ delay: 0.2 }}>
              <h2 className="text-3xl font-semibold mb-8">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">
                      Name <span className="text-primary">*</span>
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium mb-2">
                      Company <span className="text-primary">*</span>
                    </label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      required
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Company name"
                      data-testid="contact-company-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">
                      Email <span className="text-primary">*</span>
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium mb-2">
                      Phone <span className="text-primary">*</span>
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 0000000000"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-2">
                    Message <span className="text-primary">*</span>
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your requirements..."
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full md:w-auto"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl font-semibold mb-8 text-center">Find Us</h2>
            <div className="rounded-xl overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3570.5447!2d74.32!3d26.10!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjbCsDA2JzAwLjAiTiA3NMKwMTknMTIuMCJF!5e0!3m2!1sen!2sin!4v1620000000000!5m2!1sen!2sin"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="Big Bag Essentials Location"
              />
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
